import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  debounce,
  Grid,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { CustomerOrgCompany } from "../../../../common/types/apiTypes";
import { NameIdOption } from "../../../../common/types/commonTypes";
import { Nullable } from "../../../../common/types/nullable";
import { searchCustomerOrganizationsAndCompanies } from "../../../customer/customerOrganizationsSlice";

type Props = {
  onChange: (selectedCustomer: Nullable<NameIdOption>) => void;
  newRecruitedCompany: Nullable<NameIdOption> | undefined;
  initialRecruitedCompany: Nullable<NameIdOption>;
};

const TalentCustomerRecruitDialogContent = ({
  onChange,
  newRecruitedCompany,
  initialRecruitedCompany,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [customerOptions, setCustomerOptions] = useState<CustomerOrgCompany[]>(
    [],
  );

  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();

  const { status: customerOrganizationsStatus } = useAppSelector(
    (state) => state.customerOrganizations,
  );
  const { status: talentsStatus } = useAppSelector((state) => state.talents);
  const searchIsLoading = customerOrganizationsStatus === "pending";
  const saveIsLoading = talentsStatus === "pending";

  useEffect(() => {
    if (searchTerm) {
      dispatch(
        searchCustomerOrganizationsAndCompanies({ name: searchTerm }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setCustomerOptions(action.payload);
        }
      });
    }
  }, [searchTerm, dispatch]);

  const onInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setSearchTerm(term);
      }
    },
    400,
  );

  return (
    <Box minWidth="450px">
      <OverlaySpinner isLoading={saveIsLoading} />
      <Grid container>
        <Grid item container xs={12} alignItems="center" spacing={2}>
          <Grid item xs={2} textAlign="end">
            <Typography>{t("customer")}</Typography>
          </Grid>
          <Grid item xs={10}>
            <Autocomplete
              options={customerOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <CommonTextField {...params} size="small" />
              )}
              loading={searchIsLoading}
              loadingText={<LoadingSpinner />}
              value={
                newRecruitedCompany !== undefined
                  ? newRecruitedCompany
                  : initialRecruitedCompany
              }
              onChange={(_, newValue: Nullable<NameIdOption>) => {
                if (!newValue) return onChange(null);

                return onChange({
                  id: newValue.id,
                  name: newValue.name,
                });
              }}
              onInputChange={(_, newInputValue, reason) =>
                onInputChange(newInputValue, reason)
              }
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TalentCustomerRecruitDialogContent;
